/**
 * WidgetPicker
 *
 * Modal panel that displays the host page's widget set as selectable cards.
 * Registry-agnostic — accepts a WidgetRegistry prop so Trading and Market
 * Monitor each show only their own widgets.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { WidgetContract, WidgetRegistry } from '../grid-dashboard';
import styles from './WidgetPicker.module.css';

export interface WidgetPickerProps {
  isOpen: boolean;
  onClose: () => void;
  registry: WidgetRegistry;
  onAddWidget: (widgetType: string) => void;
  title?: string;
}

export const WidgetPicker: React.FC<WidgetPickerProps> = ({
  isOpen,
  onClose,
  registry,
  onAddWidget,
  title = 'Add Widget',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const widgets = registry.getAll();

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCardClick = (widgetType: string) => {
    onAddWidget(widgetType);
  };

  const getIconComponent = (iconName?: string) => {
    if (!iconName) return null;
    const pascalCase = iconName
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
    const IconComponent = (LucideIcons as any)[pascalCase];
    return IconComponent ? <IconComponent size={24} /> : null;
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.panel} ref={panelRef} role="dialog" aria-label={title}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close widget picker"
          >
            <X size={18} />
          </button>
        </div>

        <div className={styles.cardGrid}>
          {widgets.map((widget: WidgetContract) => (
            <button
              key={widget.widgetType}
              className={styles.card}
              onClick={() => handleCardClick(widget.widgetType)}
              type="button"
            >
              <div className={styles.cardIcon}>
                {getIconComponent(widget.icon) ?? '📊'}
              </div>
              <div className={styles.cardBody}>
                <span className={styles.cardName}>{widget.displayName}</span>
                <span className={styles.cardDescription}>{widget.description}</span>
              </div>
              <span className={styles.addHint}>+ Add</span>
            </button>
          ))}
        </div>

        {widgets.length === 0 && (
          <div className={styles.empty}>No widgets available</div>
        )}
      </div>
    </div>
  );
};

export default WidgetPicker;
